import { useParams } from 'react-router-dom';
import { PATHS } from 'src/router.tsx';
import { I18n } from 'src/modules/common/components/I18n/I18n.tsx';
import { MESSAGES } from 'src/modules/common/constants.ts';
import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { ConfigContext } from 'src/configContext.ts';
import { DeliveryType } from 'src/modules/checkout/components/DeliveryType.tsx';
import { RegionsEnum } from 'src/generated/operations.ts';
import { MainLayout } from 'src/modules/common/layouts/MainLayout.tsx';
import { NavigationContainer } from 'src/modules/common/components/Navigation/NavigationContainer.tsx';
import { NavigationButton } from 'src/modules/common/components/Navigation/NavigationButton.tsx';
import { Button } from 'src/modules/common/components/Button/Button.tsx';
import { RuntimeContext } from 'src/runtimeContext';
import { calculateTotals } from 'src/modules/checkout/CalculateTotals.ts';
import { addToCart } from 'src/utils/addToCart.ts';
import {
    CheckoutPageData,
} from 'src/modules/checkout/CheckoutPageMapping.ts';
import { prepareComboAttributes } from 'src/modules/checkout/prepareComboAttributes.ts';
import { ComboVariantEnum } from 'src/modules/common/ComboVariantEnum.ts';
import { Device } from 'src/common/Device/Device';
import { SubTotal } from 'src/common/SubTotal/SubTotal';
import { ValveIcon } from 'src/modules/common/icons/ValveIcon';
import { ArrowRightIcon } from 'src/modules/common/icons/ArrowRightIcon';
import { DeleteIcon } from 'src/modules/common/icons/DeleteIcon';
import { SelectedProducts } from 'src/modules/common/components/SelectedProducts/SelectedProducts';
import { MainLayoutHeader } from 'src/modules/common/components/MainLayoutHeader/MainLayoutHeader';
import { Counter } from 'src/common/Counter/Counter';
import { Accessory } from 'src/common/Accessory/Accessory';
import { Total } from 'src/common/Total/Total';
import { Price } from 'src/common/Price/Price';
import { NavigateFunction } from 'react-router/dist/lib/hooks';
import { decodeHtml } from 'src/utils/decodeHtml.ts';
import { checkoutViewOnly } from 'src/utils/isCheckoutEnabled';
import { InlineNotification } from 'src/modules/common/components/InlineNotification/InlineNotification';
import { useUserInputState } from 'src/modules/user-input/useUserInputState';
import { ValveExportExcelButton } from 'src/modules/checkout/components/ValveExportExcelButton.tsx';
import { fetchDesignData } from 'src/common/fetchDesignData.ts';
import { useApolloClient } from '@apollo/client';
import { useBelimoTranslate } from 'src/common/hooks/useBelimoTranslate.ts';
import { buildDesignDataContent } from 'src/modules/common/hooks/buildDesignDataContent.ts';
import { parseRecommended } from 'src/modules/common/utils/parseRecommended.ts';
import { convertVnomMetric } from 'src/modules/common/utils/convertVnom.ts';
import { getUnitByName } from 'src/modules/user-input/getUnitByName.ts';
import { getTargetUnits } from 'src/modules/user-input/getTargetUnits.ts';

export const CheckoutPage = ({ data, navigate, searchParams } : {
    navigate: NavigateFunction;
    data: CheckoutPageData;
    searchParams: URLSearchParams;
}) => {
    const {
        valveLabel,
        actuatorLabel,
        page,
    } = useParams();

    if (!valveLabel) {
        throw new Error('No Valve selected');
    }
    if (!actuatorLabel) {
        throw new Error('No Actuator selected');
    }
    const translate = useBelimoTranslate();
    const { state: userInputState, locationState, setLocationState } = useUserInputState([]);
    const [inProgress, setInProgress] = useState<boolean>(false);
    const { userSettings, currency, gaPush } = useContext(ConfigContext);
    const { isMobile } = useContext(RuntimeContext);

    const prepareVnom = (vnom: string) => convertVnomMetric(
        vnom,
        getUnitByName(userInputState.v100_u, getTargetUnits(userSettings.currentRegion).volumetricFlow),
        translate,
    );

    const variantDecoded = useMemo(() => {
        if (data && data.vssCombo.length > 1) {
            return ComboVariantEnum.Both;
        }
        if (data && data.vssCombo.length === 1) {
            const [p] = data.vssCombo;
            if (p.Label.indexOf('+') > 0) {
                return ComboVariantEnum.Mounted;
            }
        }
        return ComboVariantEnum.Unmounted;
    }, [data]);

    const defaultMountedValue = useMemo(() => userSettings.currentRegion === RegionsEnum.Am, [userSettings]);

    const comboCurrent = useMemo(() => locationState.combo ?? {
        mounted: variantDecoded === ComboVariantEnum.Both
            ? defaultMountedValue
            : variantDecoded === ComboVariantEnum.Mounted,
        qty: 1,
        actuator: actuatorLabel,
        valve: valveLabel,
    }, [variantDecoded, defaultMountedValue, actuatorLabel, valveLabel, locationState]);

    useEffect(() => {
        if (locationState.combo === null) {
            setLocationState({
                ...locationState,
                combo: comboCurrent,
            });
        }
    }, [comboCurrent, locationState, setLocationState]);

    const setDeliveryType = useCallback((mounted: boolean) => {
        setLocationState({
            ...locationState,
            combo: { ...comboCurrent, mounted },
        });
    }, [locationState, comboCurrent, setLocationState]);

    const setComboQty = useCallback((value: number) => {
        setLocationState({
            ...locationState,
            combo: { ...comboCurrent, qty: value },
        });
    }, [locationState, comboCurrent, setLocationState]);

    const setAccessoryQty = useCallback((name: string, value: number) => {
        setLocationState({
            ...locationState,
            selectedAccessories: locationState.selectedAccessories.map((item) => {
                const { label } = item;
                if (label === name) {
                    return { label, qty: value };
                }
                return item;
            }),
        });
    }, [locationState, setLocationState]);

    const deleteAccessory = useCallback((label: string) => {
        setLocationState({
            ...locationState,
            selectedAccessories: locationState.selectedAccessories.filter((item) => item.label !== label),
        });
    }, [locationState, setLocationState]);

    const selectMoreProductsHandler = useCallback(() => {
        navigate({
            pathname: PATHS.Home,
        });
    }, [navigate]);

    const { valve, actuator } = data.vssAccessories;

    const comboCandidate = data.vssCombo.find((item) => item.Label.includes(comboCurrent.mounted ? '+' : '/'));
    const combo = comboCandidate ?? valve;

    const {
        accessories,
        accessoriesSubtotal,
        currentCurrency,
        subtotal,
        total,
        pricePerUnit,
    } = useMemo(() => calculateTotals(
        combo,
        data.vssAccessories,
        userSettings,
        { ...locationState, combo: comboCurrent },
        currency,
    ), [locationState, comboCurrent, combo, currency, data.vssAccessories, userSettings]);

    const isCheckoutViewOnly = checkoutViewOnly(userSettings.shoppingCenter);

    const content = buildDesignDataContent({
        valve,
        userInputState,
        region: userSettings.currentRegion,
        translate,
    });
    const apollo = useApolloClient();

    const checkoutHandler = useCallback(async () => {
        if (!data) {
            return;
        }
        const {
            qty,
            mounted,
        } = comboCurrent;

        const comboLabel = comboCurrent.valve === comboCurrent.actuator
            ? comboCurrent.valve
            : `${comboCurrent.valve}${mounted ? '+' : '/'}${comboCurrent.actuator}`;

        setInProgress(true);
        const { result: designCode } = await fetchDesignData({
            content: locationState.sent || isCheckoutViewOnly ? '' : content,
            apollo,
        });

        addToCart([
            {
                label: comboLabel,
                qty,
                ...(designCode && designCode.length > 0 ? { designData: designCode } : {}),
            },
            ...locationState.selectedAccessories,
        ]);

        gaPush([
            {
                event: 'gaEvent',
                eventCategory: 'sizing tool',
                eventAction: 'step 6 - product schedule',
                eventLabel: 'add to cart or project list',
            },
            {
                event: 'EEC-addToCart',
                ecommerce: {
                    currencyCode: currentCurrency,
                    add: {
                        products: [
                            {
                                name: comboLabel,
                                id: comboLabel,
                                price: subtotal,
                                brand: 'Belimo',
                                category: 'Valve and actuator',
                                quantity: qty,
                                dimension17: [
                                    decodeHtml(data.vssAccessories.valve.family?.raw.join('')),
                                    ...(comboCurrent.valve === comboCurrent.actuator ? [] : [
                                        decodeHtml(data.vssAccessories.actuator?.family?.raw.join('')),
                                    ]),
                                ].filter((i) => i && i.length > 0).join(' and '),
                            },
                            ...accessories.map(({ accessory, price, qty: quantity }) => (
                                {
                                    name: accessory.item.Label,
                                    id: accessory.item.Label,
                                    price,
                                    brand: 'Belimo',
                                    category: 'Accessory',
                                    quantity,
                                    dimension17: decodeHtml(accessory.item.description?.raw.join('')),
                                })),
                        ],
                    },
                },
            },

        ]);
        setLocationState({
            ...locationState,
            sent: true,
        });
        setInProgress(false);
    }, [
        data,
        accessories,
        comboCurrent,
        locationState,
        isCheckoutViewOnly,
        content,
        apollo,
        gaPush,
        currentCurrency,
        subtotal,
        setLocationState,
        setInProgress,
    ]);

    const navigation = (
        <NavigationContainer className="vss-checkout-page__navigation-container" isMobile={isMobile}>
            <NavigationButton
                type="back"
                state={locationState}
                to={{
                    pathname: [
                        PATHS.Accessories,
                        valveLabel,
                        actuatorLabel,
                        page,
                    ].join('/'),
                    search: searchParams.toString(),
                }}
            >
                <I18n messageId={MESSAGES.back}/>
            </NavigationButton>

            <ValveExportExcelButton
                actuator={actuator}
                valve={valve}
                accessories={accessories}
                combo={combo}
                userSettings={userSettings}
                qty={comboCurrent.qty}
                userInputState={userInputState}
            />

            <Button
                onClick={checkoutHandler}
                disabled={locationState.sent || isCheckoutViewOnly || inProgress}
                size="lg"
                icon={(
                    <div className="vss-checkout-page__navigation-icon">
                        <ArrowRightIcon/>
                    </div>
                )}
            >
                <I18n messageId={isMobile ? MESSAGES.checkoutMobile : MESSAGES.checkout}/>
            </Button>
        </NavigationContainer>
    );

    if (locationState.sent) {
        return (
            <MainLayout>
                <MainLayoutHeader titleId={MESSAGES.selectedProductsTitle} navigation={null} breadcrumbs={null}/>

                <SelectedProducts onButtonClick={selectMoreProductsHandler}/>
            </MainLayout>
        );
    }

    const { isRecommended } = parseRecommended(valve.connectors);

    return (
        <>
            <MainLayout>
                <MainLayoutHeader
                    titleId={MESSAGES.checkoutPageTitle}
                    navigation={isMobile ? null : navigation}
                    breadcrumbs={null}
                />

                {isCheckoutViewOnly && (
                    <InlineNotification
                        type="warning"
                        message={<I18n messageId={MESSAGES.pleaseContact}/>}
                    />
                )}

                <div className="vss-checkout-page">
                    <h2 className="vss-checkout-page__title">
                        <I18n messageId={MESSAGES.checkoutValveAndActuatorTitle}/>
                    </h2>

                    <Device
                        defaultImage={<ValveIcon/>}
                        product={combo}
                        attributes={prepareComboAttributes(valve, actuator, prepareVnom)}
                        links={combo && combo.specification ? [] : [
                            valve.specification,
                            ...(actuator ? [actuator?.specification] : []),
                        ]}
                        price={isCheckoutViewOnly ? '\u2014' : (
                            <Price
                                currency={currentCurrency}
                                price={pricePerUnit}
                                locale={userSettings.currentLocale}
                            />
                        )}
                        actions={(
                            <Counter
                                value={comboCurrent.qty}
                                onChange={setComboQty}
                            />
                        )}
                    />

                    {valveLabel !== actuatorLabel && actuator && userSettings.currentRegion !== RegionsEnum.Am && (
                        <DeliveryType
                            variant={variantDecoded}
                            mounted={comboCurrent.mounted}
                            onChange={setDeliveryType}
                        />
                    )}

                    <SubTotal
                        isMobile={isMobile}
                        price={isCheckoutViewOnly ? null : subtotal}
                        locale={userSettings.currentLocale}
                        currency={currentCurrency}
                    />

                    {accessories.length > 0 && (
                        <>
                            <h2 className="vss-checkout-page__title">
                                <I18n messageId={MESSAGES.checkoutAccessoriesTitle}/>
                            </h2>

                            {accessories
                                .map(({ accessory, price, qty }) => {
                                    const { item } = accessory;
                                    return (
                                        <Accessory
                                            chip={isRecommended(item.Label)
                                                ? <I18n messageId={MESSAGES.recommended}/> : null}
                                            accessory={item}
                                            key={`${item.Label}-${item.ID}`}
                                            price={isCheckoutViewOnly ? '\u2014' : (
                                                <Price
                                                    price={price}
                                                    currency={currentCurrency}
                                                    locale={userSettings.currentLocale}
                                                />
                                            )}
                                        >
                                            <div className="vss-checkout-page__action-wrapper">
                                                <div className="vss-checkout-page__price-wrapper">
                                                    <Button
                                                        className="vss-checkout-page__button"
                                                        kind="ghost"
                                                        iconOnly={true}
                                                        onClick={() => deleteAccessory(item.Label)}
                                                    >
                                                        <div className="vss-checkout-page__icon-wrapper">
                                                            <DeleteIcon/>
                                                        </div>
                                                    </Button>
                                                </div>

                                                <div className="vss-checkout-page__counter">
                                                    <Counter
                                                        value={qty}
                                                        onChange={(value) => setAccessoryQty(item.Label, value)}
                                                    />
                                                </div>
                                            </div>
                                        </Accessory>
                                    );
                                })}
                            <SubTotal
                                isMobile={isMobile}
                                price={isCheckoutViewOnly ? null : accessoriesSubtotal}
                                locale={userSettings.currentLocale}
                                currency={currentCurrency}
                            />
                        </>
                    )}

                    <Total
                        price={isCheckoutViewOnly ? null : total}
                        locale={userSettings.currentLocale}
                        currency={currentCurrency}
                    />
                </div>
            </MainLayout>

            {isMobile && navigation}
        </>
    );
};
